import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Shield, User, Clock, Activity, UserRoundCheck } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
  PaginationPageSize,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getAllActivityLogs } from "@/services/adminService";
import { toast } from "sonner";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const years = Array.from(
  { length: 10 },
  (_, i) => new Date().getFullYear() - i
);

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cardData, setCardData] = useState({
    activeUsers: 0,
    past24HrCount: 0,
  });
  const currentDate = new Date();
  const currentMonthNumber = new Date().getMonth() + 1; // 1–12
  const currentYear = currentDate.getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(currentMonthNumber);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [pageSize, setPageSize] = useState(5);

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1); // reset to first page when page size changes
  };

  const fetchActiveLogs = async () => {
    try {
      setLoading(true);
      const res = await getAllActivityLogs({
        page,
        limit: pageSize,
        month: selectedMonth,
        year: selectedYear,
      });

      if (!res) {
        return;
      }

      setLogs(res.data);
      setTotalPages(res.totalPages);
      setTotalEvents(res.total);
      setCardData({
        activeUsers: res.activeUsers,
        past24HrCount: res.past24HrCount,
      });
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to load audit logs"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveLogs();
  }, [page, pageSize, selectedMonth, selectedYear]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Shield className="h-8 w-8" />
          Activity Logs
        </h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Activity className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total Events
              </p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-1" />
              ) : (
                <p className="text-2xl font-bold">{totalEvents}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <User className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Active Users
              </p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-1" />
              ) : (
                <p className="text-2xl font-bold">{cardData.activeUsers}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Clock className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Last 24 Hours
              </p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-1" />
              ) : (
                <p className="text-2xl font-bold">{cardData.past24HrCount}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <div className="flex justify-end  gap-2">
            {/* Month Filter */}
            <Select
              value={selectedMonth}
              onValueChange={(value) => setSelectedMonth(Number(value))}
            >
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All Months" />
              </SelectTrigger>
              <SelectContent>
                {months.map((month, index) => (
                  <SelectItem key={month} value={index + 1}>
                    {" "}
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Year Filter */}
            <Select
              value={selectedYear}
              onValueChange={(value) => setSelectedYear(value)}
            >
              <SelectTrigger className="w-24">
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          )}

          {!loading && logs.length === 0 && (
            <p className="text-sm text-gray-500 text-center">
              No activity found
            </p>
          )}

          <div className="space-y-2">
            {!loading &&
              logs.map((log) => {
                const isAdmin = log.module === "Admin";

                return (
                  <div
                    key={log._id}
                    className={`flex items-center justify-between p-2 rounded-lg border
            ${
              isAdmin
                ? "bg-primary/10 border-primary/20"
                : "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
            }`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Icon */}
                      <div
                        className={`h-10 w-10 rounded-full flex items-center justify-center
                ${
                  isAdmin ? "bg-primary/20" : "bg-blue-100 dark:bg-blue-900/40"
                }`}
                      >
                        {isAdmin ? (
                          <UserRoundCheck className="h-5 w-5 text-primary" />
                        ) : (
                          <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>

                      {/* Text */}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {log.action}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {log.description} • {log.module}
                        </p>
                      </div>
                    </div>

                    {/* Right Side */}
                    <div className="text-right">
                      <Badge
                        variant="secondary"
                        className={
                          isAdmin
                            ? "border border-primary text-primary bg-primary/10"
                            : "border border-blue-500 text-blue-600 bg-blue-100 dark:bg-blue-900/30"
                        }
                      >
                        {log.module}
                      </Badge>

                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-end sm:justify-between gap-3 mt-6">
              <PaginationPageSize
                value={pageSize}
                onValueChange={handlePageSizeChange}
              />
              <Pagination className="mx-0 justify-end">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    className={
                      page === 1 ? "pointer-events-none opacity-50" : ""
                    }
                  />
                </PaginationItem>

                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;

                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    Math.abs(pageNumber - page) <= 1
                  ) {
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          isActive={page === pageNumber}
                          onClick={() => setPage(pageNumber)}
                        >
                          {pageNumber}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  }

                  if (pageNumber === page - 2 || pageNumber === page + 2) {
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }

                  return null;
                })}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                    className={
                      page === totalPages
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                  />
                </PaginationItem>
              </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogs;
